import { useCallback, useMemo } from "react";
import {
  Stack,
  Table,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Paper,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  removeMapping,
  updateMappingPriority,
  getPrefixById,
} from "../../../services/prefixService";
import MappingRow from "./MappingRow";
import AddMappingModal from "./AddMappingModal";

interface PrefixMappingsPanelProps {
  prefixId: string;
}

export default function PrefixMappingsPanel({
  prefixId,
}: PrefixMappingsPanelProps) {
  const queryClient = useQueryClient();
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);

  const { data: prefixDetail, isLoading } = useQuery({
    queryKey: ["prefix", prefixId],
    queryFn: () => getPrefixById(prefixId),
  });

  const invalidatePrefix = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["prefix", prefixId] });
  }, [queryClient, prefixId]);

  const removeMutation = useMutation({
    mutationFn: (mappingId: string) => removeMapping(prefixId, mappingId),
    onSuccess: invalidatePrefix,
  });

  const updatePriorityMutation = useMutation({
    mutationFn: ({
      mappingId,
      priority,
    }: {
      mappingId: string;
      priority: number;
    }) => updateMappingPriority(prefixId, mappingId, priority),
    onSuccess: invalidatePrefix,
  });

  const mappings = useMemo(() => prefixDetail?.mappings ?? [], [prefixDetail]);

  const handlePriorityChange = useCallback(
    (mappingId: string, value: number | string) => {
      if (typeof value === "number") {
        updatePriorityMutation.mutate({ mappingId, priority: value });
      }
    },
    [updatePriorityMutation]
  );

  const handleRemove = useCallback(
    (mappingId: string) => {
      removeMutation.mutate(mappingId);
    },
    [removeMutation]
  );

  if (isLoading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={500} size="sm">
          Mappings
        </Text>
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={openAddModal}
        >
          Add Mapping
        </Button>
      </Group>

      {mappings.length === 0 ? (
        <Text c="dimmed" ta="center" py="md">
          No mappings yet. Click Add Mapping to create one.
        </Text>
      ) : (
        <Paper withBorder style={{ overflow: "hidden" }}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Target</Table.Th>
                <Table.Th style={{ width: 160 }}>Type</Table.Th>
                <Table.Th style={{ width: 120 }}>Priority</Table.Th>
                <Table.Th style={{ width: 60 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {mappings.map((m) => (
                <MappingRow
                  key={m.id}
                  mappingId={m.id}
                  practiceName={m.practiceName}
                  surgeryCenterLabName={m.surgeryCenterLabName}
                  physicianName={
                    m.physicianFirstName || m.physicianLastName
                      ? `${m.physicianFirstName ?? ""} ${
                          m.physicianLastName ?? ""
                        }`.trim()
                      : null
                  }
                  priority={m.priority}
                  removeLoading={removeMutation.isPending}
                  onPriorityChange={handlePriorityChange}
                  onRemove={handleRemove}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <AddMappingModal
        opened={addModalOpened}
        onClose={closeAddModal}
        prefixId={prefixId}
      />
    </Stack>
  );
}
